import { useState, useEffect } from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import api from "services/api";
import SignedDownloadButton from "pages/Clients/sections/desc/SignedDownloadButton";
import SharedIDModal from "pages/Clients/sections/ClientHeros/SharedIDModal";

import { useTranslation } from "react-i18next";

import bgImage from "assets/images/bg-user-info.jpg";
import appStoreImg from "assets/images/clients/app-store.png";

function UserHero() {
  const { t } = useTranslation();

  const [software, setSoftware] = useState(null);
  const [sharedOpen, setSharedOpen] = useState(false);
  const softwareItems = software?.items || [];
  const installerSoftware =
    softwareItems.find((item) => item.variant === "installer") || software?.current || software;
  const advancedSoftware = softwareItems.find(
    (item) => item.variant === "advanced" || item.package_type === "app_store"
  );
  const advancedDownloadUrl =
    advancedSoftware?.public_download_url || advancedSoftware?.download_url;
  const displayName = software?.name || t("clients.macos.softwareName");
  const installerVersion = installerSoftware?.version || t("clients.macos.version");
  const advancedVersion = advancedSoftware?.version || t("clients.macos.advancedVersion");

  useEffect(() => {
    api
      .get("/software/macos/")
      .then((res) => {
        setSoftware(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <MKBox
      minHeight="52vh"
      width="100%"
      sx={({ breakpoints }) => ({
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "grid",
        placeItems: "center",
        paddingTop: "5rem",
        paddingBottom: "5rem",
        [breakpoints.down("md")]: {
          paddingTop: "6rem",
          paddingBottom: "6rem",
        },
      })}
    >
      <Container>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" justifyContent="center">
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              textAlign: { xs: "center", md: "left" },
              mt: { xs: 2, md: 0 },
            }}
          >
            <MKTypography variant="h4" color="white" fontWeight="bold">
              {displayName}
            </MKTypography>

            <MKTypography variant="body2" color="white" opacity={0.8} mt={1}>
              {t("clients.macos.heroSubtitle")}
            </MKTypography>
            <MKBox
              mt={3}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1.12fr" },
                alignItems: "start",
                columnGap: { xs: 2, sm: 4 },
                rowGap: 2,
                maxWidth: 640,
                mx: { xs: "auto", md: 0 },
              }}
            >
              <MKBox
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "flex-start" }}
                gap={1.1}
                sx={{
                  minWidth: 0,
                  pr: { sm: 2 },
                }}
              >
                <MKTypography variant="caption" color="white" fontWeight="bold">
                  {t("clients.macos.installerGroup")}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.supportedSystem")}: {t("clients.macos.installerSystem")}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.currentVersionLabel")}: {installerVersion}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.installerHint")}
                </MKTypography>
                {installerSoftware?.signed_link_url ? (
                  <SignedDownloadButton
                    variant="outlined"
                    color="white"
                    linkUrl={installerSoftware.signed_link_url}
                    label={t("clients.macos.downloadInstaller")}
                  />
                ) : null}
              </MKBox>
              <MKBox
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "flex-start" }}
                gap={1.1}
                sx={{
                  minWidth: 0,
                  pl: { sm: 3 },
                  borderLeft: { xs: "none", sm: "1px solid rgba(255,255,255,0.32)" },
                }}
              >
                <MKTypography variant="caption" color="white" fontWeight="bold">
                  {t("clients.macos.advancedGroup")}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.supportedSystem")}: {t("clients.macos.advancedSystem")}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.currentVersionLabel")}: {advancedVersion}
                </MKTypography>
                <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
                  {t("clients.macos.advancedHint")}
                </MKTypography>
                <MKBox
                  display="flex"
                  flexWrap="wrap"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  gap={1}
                >
                  {advancedDownloadUrl ? (
                    <a href={advancedDownloadUrl} target="_blank" rel="noopener noreferrer">
                      <img
                        src={appStoreImg}
                        alt="Download on the App Store"
                        style={{ height: "40px", display: "block" }}
                      />
                    </a>
                  ) : null}
                  <MKButton
                    variant="outlined"
                    color="white"
                    size="small"
                    onClick={() => setSharedOpen(true)}
                    sx={{ height: "40px" }}
                  >
                    {t("clients.macos.getSharedAppleId")}
                  </MKButton>
                </MKBox>
              </MKBox>
            </MKBox>
            <SharedIDModal open={sharedOpen} onClose={() => setSharedOpen(false)} />
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            sx={{
              mt: { xs: 4, md: 0 },
              textAlign: "left",
            }}
          >
            <MKBox sx={{ maxWidth: 640 }}>
              <MKTypography variant="h5" color="white" fontWeight="bold" fontSize="1.15rem">
                {t("clients.macos.versionGuide.title")}
              </MKTypography>

              <MKTypography
                variant="caption"
                color="white"
                opacity={0.84}
                mt={1.25}
                lineHeight={1.75}
                display="block"
              >
                <strong>{t("clients.macos.versionGuide.standardTitle")}</strong>
                {t("clients.macos.versionGuide.standardBody")}
              </MKTypography>
              <MKTypography
                variant="caption"
                color="white"
                opacity={0.84}
                mt={0.8}
                lineHeight={1.75}
                display="block"
              >
                <strong>{t("clients.macos.versionGuide.advancedTitle")}</strong>
                {t("clients.macos.versionGuide.advancedBody")}
              </MKTypography>
              <MKTypography
                variant="caption"
                color="white"
                opacity={0.76}
                mt={1.1}
                lineHeight={1.7}
                display="block"
              >
                {t("clients.macos.versionGuide.appleIdHint")}
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default UserHero;
